import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import type { Layer, RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { compositeLayer } from "../pixel/blend";
import { floodFillFromSeed, selectByColor } from "../pixel/floodFill";
import { screenToDoc } from "../render/Viewport";
import { Selection, type SelectionBounds } from "../selection/Selection";
import type { PointerSample, Tool, ToolContext } from "./Tool";

// Magic Wand — click-to-select pixels of similar color via flood fill.
//
// Photoshop shortcut W. A single click produces a selection; there's no
// drag behavior (Quick Selection tool — different feature — handles drag).
//
// Modifier convention (matches MarqueeTool / LassoTool):
//   default      → replace
//   shift        → add (union)
//   alt          → subtract
//   shift + alt  → intersect
//
// `tolerance` is 0..255 max-channel; see floodFill.ts for the metric choice.
// `contiguous` toggles 4-connected flood vs. select-by-color across the
// whole layer. `antiAlias` softens edges.

export type CombineMode = "replace" | "add" | "subtract" | "intersect";

export class MagicWandTool implements Tool {
  readonly id = "magic-wand";
  readonly shortcut = "W";
  tolerance = 32; // Photoshop default
  contiguous = true;
  antiAlias = true;
  /**
   * When true, the color test samples the flattened composite of all visible
   * layers instead of only the active layer's raw pixels (Photoshop's
   * "Sample All Layers" option) — see buildDocumentComposite() (audit i160).
   */
  sampleAllLayers = false;
  /** Sample size: 1 = point sample, 3 = 3x3 average, 5 = 5x5 average. */
  sampleSize: 1 | 3 | 5 = 1;
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt-derived default computed at pointer-down; "new" (or
   * unset) falls back to the existing shift/alt modifier behavior.
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const doc = ctx.doc;
    if (!doc) return;
    if (!doc.activeLayerId) return;
    const layer = findRaster(doc.layers, doc.activeLayerId);
    if (!layer || !layer.visible) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docW = doc.width;
    const docH = doc.height;

    let local: Selection | null;

    if (this.sampleAllLayers) {
      // Sample against a flattened composite of every visible layer, in
      // document-space coordinates directly (no per-layer offset needed).
      const sx = Math.floor(p.x);
      const sy = Math.floor(p.y);
      if (sx < 0 || sy < 0 || sx >= docW || sy >= docH) return;

      const composite = buildDocumentComposite(doc.layers, docW, docH);
      const { r: sR, g: sG, b: sB, a: sA } = sampleRgba(composite, docW, docH, sx, sy, this.sampleSize);

      let mask: Uint8Array;
      if (this.contiguous) {
        mask = floodFillFromSeed(composite, docW, docH, sx, sy, {
          tolerance: this.tolerance,
          contiguous: true,
          antiAlias: this.antiAlias,
          sampleAllLayers: true,
        });
      } else if (sA === 0) {
        mask = floodFillFromSeed(composite, docW, docH, sx, sy, {
          tolerance: this.tolerance,
          contiguous: false,
          antiAlias: this.antiAlias,
          sampleAllLayers: true,
        });
      } else {
        mask = selectByColor(composite, docW, docH, sR, sG, sB, {
          tolerance: this.tolerance,
          antiAlias: this.antiAlias,
          sampleAllLayers: true,
        });
      }
      local = new Selection(docW, docH, mask);
    } else {
      // Convert to layer-local pixel coords.
      const lx = Math.floor(p.x - layer.x);
      const ly = Math.floor(p.y - layer.y);
      const lw = layer.pixels.width;
      const lh = layer.pixels.height;
      if (lx < 0 || ly < 0 || lx >= lw || ly >= lh) return;

      const data = layer.pixels.data;
      const { r: sR, g: sG, b: sB, a: sA } = sampleRgba(data, lw, lh, lx, ly, this.sampleSize);

      // Build the layer-sized mask.
      let layerMask: Uint8Array;
      if (this.contiguous) {
        layerMask = floodFillFromSeed(data, lw, lh, lx, ly, {
          tolerance: this.tolerance,
          contiguous: true,
          antiAlias: this.antiAlias,
          sampleAllLayers: false,
        });
      } else if (sA === 0) {
        // Non-contiguous transparent-seed: select all alpha=0 pixels in the
        // layer. We piggyback on floodFillFromSeed which has the special-case
        // transparent-mode logic; passing contiguous=false makes it expand to
        // every alpha=0 pixel.
        layerMask = floodFillFromSeed(data, lw, lh, lx, ly, {
          tolerance: this.tolerance,
          contiguous: false,
          antiAlias: this.antiAlias,
          sampleAllLayers: false,
        });
      } else {
        layerMask = selectByColor(data, lw, lh, sR, sG, sB, {
          tolerance: this.tolerance,
          antiAlias: this.antiAlias,
          sampleAllLayers: false,
        });
      }

      const projected = projectLayerMaskToDoc(
        layerMask,
        lw,
        lh,
        layer.x,
        layer.y,
        docW,
        docH,
      );
      local = projected === null ? null : Selection.fromMask(docW, docH, projected.mask, projected.bounds);
    }

    const combine = pickCombine(s.shiftKey, s.altKey, this.combineMode);
    const next = combineSelection(combine, doc.selection, local);
    ctx.emit(new SetSelectionCommand(next));
  }
}

/**
 * Average (or point-sample) the RGBA color at (x, y) in an RGBA8 buffer.
 * sampleSize: 1 = point sample, 3 = 3x3 average, 5 = 5x5 average — shared by
 * both the single-layer and composite (Sample All Layers) sampling paths.
 */
function sampleRgba(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  x: number,
  y: number,
  sampleSize: 1 | 3 | 5,
): { r: number; g: number; b: number; a: number } {
  const half = (sampleSize - 1) >> 1; // 0, 1, or 2
  if (half === 0) {
    const idx = (y * w + x) * 4;
    return { r: data[idx] ?? 0, g: data[idx + 1] ?? 0, b: data[idx + 2] ?? 0, a: data[idx + 3] ?? 0 };
  }
  let rSum = 0, gSum = 0, bSum = 0, aSum = 0, count = 0;
  for (let dy = -half; dy <= half; dy++) {
    for (let dx = -half; dx <= half; dx++) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const ni = (ny * w + nx) * 4;
      rSum += data[ni]!;
      gSum += data[ni + 1]!;
      bSum += data[ni + 2]!;
      aSum += data[ni + 3]!;
      count++;
    }
  }
  if (count === 0) return { r: 0, g: 0, b: 0, a: 0 };
  return {
    r: Math.round(rSum / count),
    g: Math.round(gSum / count),
    b: Math.round(bSum / count),
    a: Math.round(aSum / count),
  };
}

/**
 * Flatten every visible raster layer (bottom-to-top, per Document's layer
 * order convention — see Document.layers) into a single docW x docH RGBA8
 * buffer, honoring per-layer visibility/opacity/blend mode.
 *
 * This is a simplified composite: layer masks, clipping (`clippedToBelow`),
 * blend-if ranges, and adjustment layers are not applied. That's an
 * intentional scope limit — it's enough to make "Sample All Layers" actually
 * consider colors from non-active layers (previously a complete no-op; audit
 * i160), without requiring the full renderer pipeline here.
 */
function buildDocumentComposite(layers: readonly Layer[], docW: number, docH: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(docW * docH * 4);
  compositeLayersOnto(out, layers, docW, docH);
  return out;
}

function compositeLayersOnto(
  dst: Uint8ClampedArray,
  layers: readonly Layer[],
  docW: number,
  docH: number,
): void {
  for (const layer of layers) {
    if (!layer.visible) continue;
    if (layer.kind === "group") {
      compositeLayersOnto(dst, layer.children, docW, docH);
      continue;
    }
    if (layer.kind !== "raster") continue;
    const src = layerToDocRgba(layer, docW, docH);
    compositeLayer(dst, src, layer.blendMode, layer.opacity);
  }
}

function layerToDocRgba(layer: RasterLayer, docW: number, docH: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(docW * docH * 4);
  const { pixels } = layer;
  const lw = pixels.width;
  const lh = pixels.height;
  const lx = layer.x;
  const ly = layer.y;
  const x0 = Math.max(0, lx);
  const y0 = Math.max(0, ly);
  const x1 = Math.min(docW, lx + lw);
  const y1 = Math.min(docH, ly + lh);
  for (let dy = y0; dy < y1; dy++) {
    const srcRow = (dy - ly) * lw;
    const dstRow = dy * docW;
    for (let dx = x0; dx < x1; dx++) {
      const si = (srcRow + (dx - lx)) * 4;
      const di = (dstRow + dx) * 4;
      out[di] = pixels.data[si] ?? 0;
      out[di + 1] = pixels.data[si + 1] ?? 0;
      out[di + 2] = pixels.data[si + 2] ?? 0;
      out[di + 3] = pixels.data[si + 3] ?? 0;
    }
  }
  return out;
}

function pickCombine(shift: boolean, alt: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt-derived default. "new" (or no override) falls through to
  // the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return "replace";
}

function combineSelection(
  mode: CombineMode,
  original: Selection | null,
  local: Selection | null,
): Selection | null {
  switch (mode) {
    case "replace":
      return local && !local.isEmpty() ? local : null;
    case "add":
      if (!original) return local && !local.isEmpty() ? local : null;
      if (!local || local.isEmpty()) return original;
      return original.union(local);
    case "subtract":
      if (!original) return null;
      if (!local || local.isEmpty()) return original;
      return original.subtract(local);
    case "intersect":
      if (!original) return null;
      if (!local || local.isEmpty()) return null;
      return original.intersect(local);
  }
}

// Copy a layer-sized mask into the layer's footprint within a doc-sized
// mask. Handles clipping when the layer overhangs the document. Returns
// the doc-sized mask plus its tight bounds, or null if no pixels land
// inside the document.
function projectLayerMaskToDoc(
  layerMask: Uint8Array,
  lw: number,
  lh: number,
  lOffX: number,
  lOffY: number,
  docW: number,
  docH: number,
): { mask: Uint8Array; bounds: SelectionBounds } | null {
  const docMask = new Uint8Array(docW * docH);
  // Intersect [lOffX, lOffX+lw) × [lOffY, lOffY+lh) with the doc.
  const x0 = Math.max(0, lOffX);
  const y0 = Math.max(0, lOffY);
  const x1 = Math.min(docW, lOffX + lw);
  const y1 = Math.min(docH, lOffY + lh);
  if (x1 <= x0 || y1 <= y0) return null;

  let minX = docW;
  let minY = docH;
  let maxX = -1;
  let maxY = -1;
  for (let dy = y0; dy < y1; dy++) {
    const srcRow = (dy - lOffY) * lw;
    const dstRow = dy * docW;
    for (let dx = x0; dx < x1; dx++) {
      const v = layerMask[srcRow + (dx - lOffX)] ?? 0;
      if (v !== 0) {
        docMask[dstRow + dx] = v;
        if (dx < minX) minX = dx;
        if (dy < minY) minY = dy;
        if (dx > maxX) maxX = dx;
        if (dy > maxY) maxY = dy;
      }
    }
  }
  if (maxX < 0) return null;
  return {
    mask: docMask,
    bounds: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 },
  };
}
